import React from 'react';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">

      {/* HEADER */}
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center gap-2.5 group">
              <div className="w-9 h-9 bg-[#8656cd] rounded-[5px] flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
                <div className="w-[15px] h-[15px] bg-[#222222] rounded-full" />
              </div>
              <span className="text-xl font-bold text-slate-900 tracking-tight">Clicknrec</span>
            </a>
            <a href="/" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
              ← Back to home
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <div className="bg-slate-50 border-b border-slate-200 py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest text-violet-600 mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">Privacy Policy</h1>
          <p className="text-slate-500 text-base">Last updated: June 2026 · Questions? <a href="mailto:record@clicknrec.com" className="text-violet-600 hover:underline">record@clicknrec.com</a></p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-slate max-w-none">

          <p className="text-lg text-slate-600 leading-relaxed mb-12">
            This Privacy Policy describes how Clicknrec ("we," "us," or "our") collects, uses, and discloses your information when you use our screen recording application and services (the "Service").
          </p>

          {[
            {
              number: '01',
              title: 'Information We Collect',
              content: (
                <ul className="space-y-4 text-slate-600">
                  <li><strong className="text-slate-900">Screencast Content:</strong> When you record your screen, we temporarily access and process the content of the active tab or selected screen area — including audio and visual information from your recording.</li>
                  <li><strong className="text-slate-900">Transcription Data:</strong> Our AI transcription feature processes audio to generate text for trimming and editing. We track token usage based on transcriptions for billing purposes.</li>
                  <li><strong className="text-slate-900">User Preferences & Settings:</strong> We store your preferences such as default recording quality, chosen backgrounds, and session progress.</li>
                  <li><strong className="text-slate-900">Technical Data:</strong> Non-personally identifiable data such as browser type, OS, and extension version to help us debug and improve.</li>
                  <li><strong className="text-slate-900">Feedback & Support:</strong> Information you provide when contacting support, including email address and the nature of your query.</li>
                </ul>
              ),
            },
            {
              number: '02',
              title: 'How We Use Your Information',
              content: (
                <ul className="space-y-3 text-slate-600">
                  <li>To enable recording, AI trimming, transcription, backgrounds, and video export</li>
                  <li>To analyze usage patterns and develop new features</li>
                  <li>To respond to support inquiries and troubleshoot issues</li>
                  <li>To track token usage for free plan and manage subscriptions</li>
                  <li>To notify you about recording status and exports</li>
                  <li>To improve our AI trimming and transcription algorithms</li>
                </ul>
              ),
            },
            {
              number: '03',
              title: 'How We Share Your Information',
              content: (
                <ul className="space-y-4 text-slate-600">
                  <li><strong className="text-slate-900">Service Providers:</strong> We may share information with third-party providers (e.g. cloud hosting, analytics) who are obligated to protect your data.</li>
                  <li><strong className="text-slate-900">Legal Reasons:</strong> We may disclose information when required by law or to protect our rights, prevent fraud, or comply with legal obligations.</li>
                  <li><strong className="text-slate-900">With Your Consent:</strong> We may share for any other purpose with your explicit consent.</li>
                </ul>
              ),
            },
            {
              number: '04',
              title: 'Data Retention',
              content: (
                <p className="text-slate-600">We retain screencast content and transcription data only as long as necessary to provide the Service. Once your screencast is exported or deleted, raw content and transcription data are removed from our systems. User preferences are retained to provide a consistent experience.</p>
              ),
            },
            {
              number: '05',
              title: 'Security',
              content: (
                <p className="text-slate-600">We implement reasonable technical and organizational measures to protect your information from unauthorized access, disclosure, alteration, or destruction. No internet transmission is completely secure, so we cannot guarantee absolute security.</p>
              ),
            },
            {
              number: '06',
              title: 'Your Choices and Rights',
              content: (
                <ul className="space-y-3 text-slate-600">
                  <li><strong className="text-slate-900">Your Recordings:</strong> You have full control over your screencasts within the editor and can delete them at any time.</li>
                  <li><strong className="text-slate-900">Notifications:</strong> Manage notification settings through your browser or Clicknrec extension settings.</li>
                  <li><strong className="text-slate-900">Feedback:</strong> Provide suggestions through our feedback form to help shape the product.</li>
                </ul>
              ),
            },
            {
              number: '07',
              title: 'Browser Permissions',
              content: (
                <ul className="space-y-4 text-slate-600">
                  <li><strong className="text-slate-900">activeTab:</strong> Temporary access to the active tab to initiate screen recordings without broader access to all browsing data.</li>
                  <li><strong className="text-slate-900">tabs:</strong> Required to capture content from specific tabs selected by the user.</li>
                  <li><strong className="text-slate-900">downloads:</strong> Facilitates export of finished screencasts to your local machine.</li>
                  <li><strong className="text-slate-900">storage:</strong> Saves preferences, settings, and intermediate recording data locally.</li>
                  <li><strong className="text-slate-900">notifications:</strong> Informs you about recording status, processing, and export completion.</li>
                  <li><strong className="text-slate-900">host_permissions:</strong> Enables advanced recording by interacting with the DOM of the active tab to capture visual elements accurately.</li>
                </ul>
              ),
            },
            {
              number: '08',
              title: 'Remote Code',
              content: (
                <p className="text-slate-600">Clicknrec uses remote code to dynamically update AI trimming algorithms and features without requiring manual extension updates. All remote code is securely fetched and vetted to maintain your privacy and security.</p>
              ),
            },
            {
              number: '09',
              title: 'Changes to This Policy',
              content: (
                <p className="text-slate-600">We may update this Privacy Policy from time to time. Changes will be posted on this page. We advise reviewing this policy periodically.</p>
              ),
            },
            {
              number: '10',
              title: 'Contact Us',
              content: (
                <p className="text-slate-600">If you have questions about this Privacy Policy, email us at <a href="mailto:record@clicknrec.com" className="text-violet-600 hover:underline font-medium">record@clicknrec.com</a>.</p>
              ),
            },
          ].map((section) => (
            <div key={section.number} className="mb-12 pb-12 border-b border-slate-100 last:border-0 last:mb-0 last:pb-0">
              <div className="flex items-start gap-5">
                <span className="text-xs font-bold text-violet-400 tracking-widest mt-1.5 shrink-0">{section.number}</span>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">{section.title}</h2>
                  {section.content}
                </div>
              </div>
            </div>
          ))}

        </div>
      </div>

      {/* FOOTER */}
      <footer className="py-10 bg-slate-50 border-t border-slate-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#8656cd] rounded-[5px] flex items-center justify-center">
              <div className="w-[15px] h-[15px] bg-[#222222] rounded-full" />
            </div>
            <span className="text-xl font-bold text-slate-900">Clicknrec</span>
          </div>
          <div className="flex gap-6 text-sm font-medium text-slate-500">
            <a href="https://calendly.com/record-clicknrec/30min" className="hover:text-slate-900 transition-colors">Contact</a>
            <a href="/privacy" className="hover:text-slate-900 transition-colors">Privacy</a>
          </div>
          <div className="text-sm text-slate-400">© {new Date().getFullYear()} Clicknrec. All rights reserved.</div>
        </div>
      </footer>

    </div>
  );
};

export default Privacy;